import { preload } from '../nojq/_load.js';

/**
 * Cached Promise to ensure Chart library is loaded only once.
 * @type {Promise<typeof window.Chart> | null}
 */
let chartPromise = null;

/**
 * Loads the Chart.js library (via a custom build at `_chart.js`), registers
 * a center-text plugin, and resolves with the loaded Chart constructor.
 * Subsequent calls return the same resolved Promise.
 *
 * @returns {Promise<typeof window.Chart>}
 */
export function loadChart() {
    if (!chartPromise) {
        chartPromise = preload({
            files: [window.__INITIAL_STATE__.staticUrl + 'javascripts/desktop/libs/_chart.js'],
            type: 'js',
        })
            .then(() => {
                // Register a plugin that draws centered text inside a doughnut/pie chart
                window.Chart.pluginService.register({
                    beforeDraw: function (chart) {
                        const centerConfig = chart.config.options.elements?.center;
                        if (!centerConfig) {
                            return;
                        }

                        const ctx = chart.chart.ctx;
                        const txt = centerConfig.text || '';
                        const fontStyle = centerConfig.fontStyle || 'Arial';
                        const color = centerConfig.color || '#000';
                        const maxFontSize = centerConfig.maxFontSize || 75;
                        const sidePadding = centerConfig.sidePadding || 20; // as percentage
                        const sidePaddingCalculated = (sidePadding / 100) * (chart.innerRadius * 2);

                        // Start with a base font of 30px
                        ctx.font = '30px ' + fontStyle;

                        // Measure text width and available width
                        const stringWidth = ctx.measureText(txt).width;
                        const elementWidth = chart.innerRadius * 2 - sidePaddingCalculated;
                        const widthRatio = elementWidth / stringWidth;
                        const newFontSize = Math.floor(30 * widthRatio);
                        const elementHeight = chart.innerRadius * 2;

                        // Choose font size not exceeding element height or maxFontSize
                        let fontSizeToUse = Math.min(newFontSize, elementHeight, maxFontSize);
                        const minFontSize = centerConfig.minFontSize ?? 20;
                        const lineHeight = centerConfig.lineHeight || 25;

                        let wrapText = false;
                        if (fontSizeToUse < minFontSize) {
                            fontSizeToUse = minFontSize;
                            wrapText = true;
                        }

                        // Set text alignment
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.font = fontSizeToUse + 'px ' + fontStyle;
                        ctx.fillStyle = color;

                        // Determine center point
                        let centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
                        let centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

                        if (!wrapText) {
                            ctx.fillText(txt, centerX, centerY);
                            return;
                        }

                        // Wrap text into multiple lines if needed
                        const words = txt.split(' ');
                        let line = '';
                        const lines = [];

                        for (let n = 0; n < words.length; n++) {
                            const testLine = line + words[n] + ' ';
                            const metrics = ctx.measureText(testLine);
                            const testWidth = metrics.width;
                            if (testWidth > elementWidth && n > 0) {
                                lines.push(line);
                                line = words[n] + ' ';
                            } else {
                                line = testLine;
                            }
                        }
                        lines.push(line);

                        // Adjust vertical starting point
                        centerY -= (lines.length / 2) * lineHeight;

                        // Draw each line
                        lines.forEach((l) => {
                            ctx.fillText(l, centerX, centerY);
                            centerY += lineHeight;
                        });
                    },
                });

                return window.Chart;
            })
            .catch((err) => {
                // If preload fails, reset chartPromise so that future attempts can retry
                chartPromise = null;
                return Promise.reject(err);
            });
    }

    return chartPromise;
}
